import { Logo } from "@/components/Logo";
import { Avatar } from "@/components/PhotoUpload";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, monthLabel, dateLabel, PAYMENT_STATUS, ATTEND_STATUS, LEVEL_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Star, LogOut, BookOpen, Wallet, UserCheck } from "lucide-react";

export default function ParentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/portal/me").then((r) => setData(r.data)); }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const { student, journals, payments, attendance } = data;
  const hadir = attendance.filter((a) => a.status === "hadir").length;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="hidden sm:inline text-sm text-slate-400 font-semibold border-l border-amber-200 pl-3">Portal Wali</span>
          </div>
          <Button variant="ghost" data-testid="portal-logout-btn" onClick={handleLogout} className="rounded-xl text-rose-600"><LogOut className="w-4 h-4 mr-1" /> Keluar</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <Card className="p-6 rounded-2xl border-amber-100">
          <div className="flex items-center gap-4">
            <Avatar src={student?.photo} name={student?.name || "?"} size="w-16 h-16" rounded="rounded-3xl" text="text-2xl" />
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">{student?.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className={`rounded-full ${LEVEL_META[student?.level]?.cls}`}>{student?.level}</Badge>
                {student?.class_label && <span className="text-sm text-slate-400">{student.class_label}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {(student?.schedule || []).map((s, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-sky-50 text-sky-700 font-semibold">{s.day} · {s.time}</span>)}
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 rounded-2xl border-amber-100 text-center"><UserCheck className="w-5 h-5 text-emerald-500 mx-auto" /><div className="font-display text-2xl font-bold mt-1">{hadir}</div><div className="text-xs text-slate-400">Kehadiran</div></Card>
          <Card className="p-4 rounded-2xl border-amber-100 text-center"><BookOpen className="w-5 h-5 text-violet-500 mx-auto" /><div className="font-display text-2xl font-bold mt-1">{journals.length}</div><div className="text-xs text-slate-400">Jurnal</div></Card>
          <Card className="p-4 rounded-2xl border-amber-100 text-center"><Wallet className="w-5 h-5 text-orange-500 mx-auto" /><div className="font-display text-2xl font-bold mt-1">{payments.filter((p) => p.status === "lunas").length}</div><div className="text-xs text-slate-400">Lunas</div></Card>
        </div>

        <Tabs defaultValue="journal">
          <TabsList className="rounded-xl bg-amber-50">
            <TabsTrigger value="journal" className="rounded-lg">Jurnal Harian</TabsTrigger>
            <TabsTrigger value="payment" className="rounded-lg">Kartu Iuran</TabsTrigger>
            <TabsTrigger value="attendance" className="rounded-lg">Kehadiran</TabsTrigger>
          </TabsList>

          <TabsContent value="journal" className="mt-4 space-y-3">
            {journals.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">Belum ada jurnal.</p> :
              journals.map((j) => (
                <Card key={j.id} className="p-4 rounded-xl border-amber-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-orange-600">{dateLabel(j.date)}</span>
                    <div className="flex gap-0.5">{[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= j.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div>
                  </div>
                  <div className="font-semibold text-slate-800 mt-1">{j.material}</div>
                  {j.homework && <div className="text-sm text-slate-500 mt-1">PR: {j.homework}</div>}
                  {j.teacher_note && <div className="text-sm text-slate-500 mt-1 italic">"{j.teacher_note}"</div>}
                </Card>
              ))}
          </TabsContent>

          <TabsContent value="payment" className="mt-4 space-y-2">
            {payments.map((p) => (
              <Card key={p.id} className="p-3 rounded-xl border-amber-100 flex items-center justify-between">
                <div><div className="text-sm font-semibold text-slate-800">{monthLabel(p.month)}</div><div className="text-xs text-slate-400">{rupiah(p.amount_paid)} / {rupiah(p.amount_due)}</div></div>
                <Badge variant="outline" className={`rounded-full ${PAYMENT_STATUS[p.status]?.cls}`}>{PAYMENT_STATUS[p.status]?.label}</Badge>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="attendance" className="mt-4 space-y-2">
            {attendance.map((a) => (
              <Card key={a.id} className="p-3 rounded-xl border-amber-100 flex items-center justify-between">
                <span className="text-sm text-slate-700">{dateLabel(a.date)}</span>
                <Badge variant="outline" className={`rounded-full ${ATTEND_STATUS[a.status]?.cls}`}>{ATTEND_STATUS[a.status]?.label}</Badge>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
